<template>
    <div class="global-add-widget-dialog__wrapper" data-testid="add-widget-dialog-wrapper">
        <RedesignBaseModal
            :visible="showDialog"
            :top="'8vh'"
            append-to-body
            :destroy-on-close="true"
            width="960px"
            data-testid="add-widget-dialog"
            wrapper-classes="full-height dashboard-widgets-modal"
            @close="closeModal"
        >
            <template #title>
                <div class="text-xl px-2" data-testid="add-widget-dialog-title">
                    {{ title }}
                </div>
            </template>
            <div
                v-if="groupToEdit"
                class="dashboard-widgets-modal__content bg-main-bg"
                data-testid="add-widget-dialog-content"
            >
                <WidgetTemplateCategories
                    v-if="currentStepNumber === TEMPLATE_CATEGORIES"
                    data-testid="widget-template-categories"
                    :categories="widgetTemplateCategories"
                    @select-category="onSelectCategory"
                />
                <WidgetTemplateCategoryWidgets
                    v-if="currentStepNumber === CATEGORY_PREVIEW"
                    v-model="selectedTemplates"
                    data-testid="widget-category-widgets"
                    :category="selectedCategory"
                    :total-widgets="totalWidgetsCount"
                    @update:step="onUpdateStep"
                />
                <WidgetTemplatesSettings
                    v-if="currentStepNumber === TEMPLATES_SETUP"
                    data-testid="widget-templates-settings"
                    :loading="submitloading || loading"
                    :selected-templates="selectedTemplates"
                    :total-widgets="totalWidgetsCount"
                    @on-finish="onSubmit"
                    @update:step="onUpdateStep"
                    @update:count="onUpdateSelectedCount"
                />
            </div>
            <template
                v-if="currentStepNumber !== TEMPLATE_CATEGORIES"
                #footer
            >
                <div class="flex-grow w-full flex justify-between items-center gap-3">
                    <div class="flex items-center gap-2">
                        <span class="leading-none">
                            {{ $t('common.alreadyUsed') }} <span
                            class="leading-none bg-light-bg px-2 py-1.5 border border-ui-lines rounded-full inline-flex items-center justify-center"
                        >{{
                                groupToEdit?.WidgetList?.length || 0
                            }}</span>
                        </span>
                        <span class="leading-none">
                            {{ $t('general.selected') }} <span
                            class="leading-none bg-light-bg px-2 py-1.5 border border-ui-lines rounded-full inline-flex items-center justify-center"
                        >{{
                                selectedTemplatesNumber
                            }}</span>
                        </span>
                    </div>
                    <div class="flex gap-2 items-center">
                        <RedesignBaseButton
                            color="secondary"
                            type="outline"
                            @click="closeModal"
                        >
                            {{ $t('common.cancel') }}
                        </RedesignBaseButton>

                        <portal-target name="widget-modal-actions" />
                    </div>
                </div>
            </template>
        </RedesignBaseModal>
    </div>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { computed, ref } from 'vue'
import i18n from '@/i18n'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { WidgetConfig, WidgetGroupList } from '@/types/dashboard'
import WidgetTemplateCategories from '@/components/RedesignComponents/Widgets/Create/WidgetTemplateCategories.vue'
import { IWidgetTemplateCategory, IWidgetTemplateListItem } from '@/types/templates'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import WidgetTemplateCategoryWidgets
    from '@/components/RedesignComponents/Widgets/Create/WidgetTemplateCategoryWidgets.vue'
import WidgetTemplatesSettings from '@/components/RedesignComponents/Widgets/Create/WidgetTemplatesSettings.vue'
import {
    CATEGORY_PREVIEW,
    MAXIMUM_RANGE_DEFAULT,
    TEMPLATE_CATEGORIES,
    TEMPLATES_SETUP
} from '@/components/RedesignComponents/Widgets/widgets.enum'
import { widgetModel } from '@/models/instances'
import { removeWidgetParameterValue } from '@/components/RedesignComponents/Widgets/widget.helper'
import { isClockAndDateWidget, isCounterAgentsInStatus, isQueueDashboardWidget, isQueueGauge } from '@/helpers/widgetUtils'
import { getNotEmptyTemplateCategories, getWidgetTemplates } from '@/composables/globalData/useTemplatesCategoryData'
import { ActiveDashboard } from '@/composables/globalData/useDashoardsData'
import { setupClockAndDateWidgetRequiredData } from '@/enum/widgetPredefineData'
import useConfirmModal from '@/composables/useConfirmModal'

type TSelectedTemplate = Record<number, { count: number, template: IWidgetTemplateListItem }>
type TSettingsFinalEmitType = {
    widgetList: Array<IWidgetTemplateListItem>
    uniqTemplatesConfigs: Array<WidgetConfig>
    widgetUniqueTemplateModel: Partial<IWidgetTemplateListItem>
}

defineProps<{
    loading?: boolean
}>()
const emit = defineEmits(['save', 'close'])

/* Data */
const showDialog = ref<boolean>(false)
const currentStepNumber = ref(TEMPLATE_CATEGORIES)
const submitloading = ref(false)

const groupToEdit = ref<WidgetGroupList | undefined>(undefined)
const selectedCategory = ref<IWidgetTemplateCategory | undefined>(undefined)
const selectedTemplates = ref<TSelectedTemplate>({})

/* Computed */
const title = computed(() =>
    currentStepNumber.value === TEMPLATE_CATEGORIES || !selectedCategory.value
        ? i18n.t('dashboard.addWidgets')
        : i18n.t(String(selectedCategory.value.TemplatesCategoryNameContentTag))
)
const widgetTemplateCategories = computed(() => {
    const notEmptyCategories = getNotEmptyTemplateCategories()
    const widgetTemplatesList = getWidgetTemplates()
    return notEmptyCategories.map((category) => {
        return {
            ...category,
            templates: widgetTemplatesList.reduce((acc: Array<IWidgetTemplateListItem>, item: IWidgetTemplateListItem) => {
                if (item.CategoryList.includes(category.CategoryID || -1)) {
                    acc.push({
                        ...item,
                        WidgetTemplateColumns: []
                    })
                }

                return acc
            }, [])
        }
    })
})
const selectedTemplatesNumber = computed(() => {
    return Object.values(selectedTemplates.value).reduce((acc, i) => (acc += i.count || 0), 0)
})
const totalWidgetsCount = computed(() => {
    return selectedTemplatesNumber.value + (groupToEdit.value?.WidgetList?.length || 0)
})
const hasUnsavedChanges = computed(() => {
    // User has changes if they moved past the first step or selected any templates
    return currentStepNumber.value !== TEMPLATE_CATEGORIES || selectedTemplatesNumber.value > 0
})

/* Methods */
function onUpdateStep (step: number) {
    currentStepNumber.value = step
    if (step === TEMPLATE_CATEGORIES) {
        selectedCategory.value = undefined
    }
}

async function handleCloseAttempt () {
    if (hasUnsavedChanges.value) {
        const isConfirmed = await useConfirmModal({
            header: String(i18n.t('general.defaultConfirmation')),
            message: String(i18n.t('dialog.confirm.add.cancel')),
            type: 'confirm'
        })
        if (isConfirmed) {
            forceCloseModal()
        }
    } else {
        forceCloseModal()
    }
}

function forceCloseModal () {
    showDialog.value = false
    submitloading.value = false
    groupToEdit.value = undefined
    selectedCategory.value = undefined
    selectedTemplates.value = {}
    currentStepNumber.value = TEMPLATE_CATEGORIES
}

async function closeModal () {
    await handleCloseAttempt()
}

function openModal (group: WidgetGroupList) {
    currentStepNumber.value = TEMPLATE_CATEGORIES
    groupToEdit.value = { ...group }
    showDialog.value = true
}

function onSelectCategory(category: IWidgetTemplateCategory) {
    selectedCategory.value = { ...category }
    currentStepNumber.value = CATEGORY_PREVIEW
}

function onUpdateSelectedCount(templateId: number, newCount: number) {
    if (!selectedTemplates.value[templateId]) {
        return
    }
    const _list = { ...selectedTemplates.value }
    if (newCount > 0) {
        _list[templateId].count = newCount
    } else if (newCount === 0) {
        delete _list[templateId]
    }
    selectedTemplates.value = { ..._list }
}

function onSubmit(finalData: TSettingsFinalEmitType) {
    submitloading.value = true
    let index = groupToEdit.value?.WidgetList?.length || 0
    const widgets = finalData.widgetList.map(widget => {
        const templateWidth = get(widget, 'TemplateSettings.DefaultWidthPrecentage', '100')
        const templateHeight = get(widget, 'TemplateSettings.DefaultHeightPixels', '160')
        const datTypeId = widget.DataType.DataTypeID
        const widgetLayout = widget.WidgetLayout || {}

        widgetLayout.GridLayout = {
            ...widgetLayout.GridLayout,
            width: Math.min(Math.ceil(64 * Number(templateWidth) / 100), 64),
            height: Math.floor(Number(templateHeight) / 80) * 2
        }

        const newWidget: Partial<IWidgetTemplateListItem> = widgetModel(widget.TemplateID, widget.TemplateName, groupToEdit.value?.WidgetGroupID, ActiveDashboard.value, {
            Order: (groupToEdit.value?.WidgetList?.length || 0) + index++,
            DataTypeID: datTypeId,
            Endpoint: widget.Endpoint,
            DefaultRefreshInterval: widget.DefaultRefreshInterval,
            ...widgetLayout,
            TemplateSettings: widget.TemplateSettings,
            settings: {}
        })

        if (!widget.customSetup) {
            newWidget.WidgetTime = {
                ...finalData.widgetUniqueTemplateModel.WidgetTime
            }
            newWidget.WidgetLayout = { ...newWidget.WidgetLayout }
            newWidget.WidgetConfig = [ ...widget.DefaultWidgetConfig ].map(i => {
                // Match by ParameterName to be consistent with uniqBy deduplication in getUniqTemplatesConfigs
                const uniqueData = finalData.uniqTemplatesConfigs.find(param => param.ParameterName === i.ParameterName && param.ParameterType === i.ParameterType)
                if (uniqueData) {
                    return removeWidgetParameterValue({
                        ...i,
                        ...uniqueData,
                        ParameterID: i.ParameterID
                    })
                }
                return removeWidgetParameterValue({ ...i })
            })
            if (isQueueDashboardWidget(widget)) {
                const { WidgetLayout } = finalData.widgetUniqueTemplateModel
                newWidget.WidgetLayout.ShowStatistics = WidgetLayout?.ShowStatistics
                newWidget.WidgetLayout.SumOfOthers = WidgetLayout?.SumOfOthers
                newWidget.WidgetLayout.AbsoluteNumbers = WidgetLayout?.AbsoluteNumbers
            }
            if (isCounterAgentsInStatus(widget.DataType)) {
                newWidget.WidgetLayout.status = finalData.widgetUniqueTemplateModel.WidgetLayout?.status
            }
            if (isQueueGauge(widget)) {
                newWidget.WidgetLayout.maximumRange = MAXIMUM_RANGE_DEFAULT
                newWidget.WidgetLayout.showWaitingTimeTable = true
            }
        } else {
            newWidget.WidgetTime = { ...(widget.WidgetTime || widget.DefaultWidgetTime) }
            newWidget.WidgetConfig = (widget.WidgetConfig || widget.DefaultWidgetConfig)?.map(i => removeWidgetParameterValue(i))
        }

        if (isClockAndDateWidget(widget.DataType)) {
            setupClockAndDateWidgetRequiredData(newWidget as IWidgetTemplateListItem)
        }

        // if (WidgetPredefineData[datTypeId]) {
        //     WidgetPredefineData[datTypeId](newWidget)
        // }

        return newWidget
    })
    submitloading.value = true

    emit('save', widgets, groupToEdit.value)
}

defineExpose({
    open: openModal,
    forceClose: forceCloseModal
})
</script>
